import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge, Button, PageHeader, EngineKeyline } from '@/components/ui/primitives';
import { Score } from '@/components/Score';
import { InsightCard } from '@/components/InsightCard';
import { ErrorState, LoadingState } from '@/components/DataState';
import { fetchLeadDetail } from '@/data/sources';
import { formatRelative } from '@/lib/utils';
import type { LeadEvent } from '@/types';

/* /sales/leads/:id — the record "dossier" canvas (brief §6.3). Shows the
 * evidence-first score, an editable field pattern, the lead timeline, and the
 * flagship promote→CRM action. :id is the lead_ref in real mode. */

function TimelineItem({ ev }: { ev: LeadEvent }) {
  return (
    <li className="relative pl-6">
      <span className="absolute left-1.5 top-1.5 h-2 w-2 rounded-full bg-engine" aria-hidden />
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-text-primary">{ev.type}</span>
        <span className="text-[11px] text-text-tertiary">{formatRelative(ev.at)}</span>
      </div>
      <p className="text-sm text-text-secondary">{ev.detail}</p>
    </li>
  );
}

export function LeadDetailPage() {
  const { id } = useParams();
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['lead', id],
    queryFn: () => fetchLeadDetail(id ?? ''),
    enabled: !!id,
  });

  if (isPending) return <LoadingState rows={6} />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  if (!data) {
    return (
      <div className="py-16 text-center text-text-secondary">
        Lead not found. <Link to="/sales/leads" className="text-engine underline">Back to leads</Link>
      </div>
    );
  }

  const { lead, events } = data;

  return (
    <div>
      <nav className="mb-3 text-xs text-text-tertiary">
        <Link to="/sales/leads" className="hover:text-text-primary">Leads</Link> ›{' '}
        <span className="text-text-secondary">{lead.business_name}</span>
      </nav>

      <PageHeader
        title={lead.business_name}
        subtitle={[lead.practitioner, lead.modality, lead.location].filter(Boolean).join(' · ')}
        actions={
          <>
            <Button variant="secondary">Edit</Button>
            {/* the manual operator gate — an operator action, not the machine (§2.1.1) */}
            <Button variant="primary">Promote to CRM →</Button>
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left: dossier facts */}
        <div className="space-y-6 lg:col-span-2">
          <Card className="overflow-hidden">
            <EngineKeyline />
            <div className="grid grid-cols-2 gap-4 p-5 sm:grid-cols-3">
              <Field label="Fit score">
                {lead.fit_score !== null ? (
                  <Score value={lead.fit_score} outOf={100} evidence={lead.fit_evidence} label={`${lead.business_name} fit`} size="lg" />
                ) : (
                  <span className="text-sm text-text-tertiary">Not scored yet</span>
                )}
              </Field>
              <Field label="Priority">
                <Badge tone={lead.priority === 'high' ? 'danger' : lead.priority === 'medium' ? 'warning' : 'neutral'}>
                  {lead.priority}
                </Badge>
              </Field>
              <Field label="Status">
                <Badge tone="success">{lead.status}</Badge>
              </Field>
              <Field label="Email">{lead.email || '—'}</Field>
              <Field label="Website">{lead.website || '—'}</Field>
              <Field label="Campaign">{lead.campaign || '—'}</Field>
              <Field label="Owner">{lead.owner || '—'}</Field>
            </div>
            <div className="border-t border-border-subtle px-5 py-2 text-[11px] text-text-tertiary">
              Every field is operator-editable. Edits are audit-logged with actor + prior value.
            </div>
          </Card>

          {/* Timeline */}
          <Card className="p-5">
            <h2 className="mb-3 text-sm font-semibold text-text-primary">Timeline</h2>
            {events.length ? (
              <ol className="space-y-4 border-l border-border-subtle">
                {events.map((ev) => (
                  <TimelineItem key={ev.id} ev={ev} />
                ))}
              </ol>
            ) : (
              <p className="text-sm text-text-tertiary">
                No events yet — sends, opens, replies and notes will appear here as they happen.
              </p>
            )}
          </Card>
        </div>

        {/* Right: copilot proposal for this record */}
        <div className="space-y-3">
          {lead.next_action ? (
            <InsightCard
              title="Suggested next move"
              confidence="high"
              actions={
                <>
                  {/* human decides — the accept is the operator's, rendered in ink */}
                  <Button variant="primary" className="h-8 px-2 text-xs">Approve</Button>
                  <Button variant="ghost" className="h-8 px-2 text-xs">Edit</Button>
                  <Button variant="ghost" className="h-8 px-2 text-xs">Discard</Button>
                </>
              }
            >
              {lead.next_action}
            </InsightCard>
          ) : (
            <div className="rounded-lg border border-dashed border-border-default bg-surface p-4 text-xs text-text-tertiary">
              No suggested next move yet — one appears when the engine records a next action for
              this lead.
            </div>
          )}
          <div className="rounded-lg border border-border-subtle bg-surface p-4 text-xs text-text-tertiary">
            Source attribution travels with a promote: campaign, lead ref and a score snapshot land on the new CRM
            contact, company and opportunity.
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-text-tertiary">{label}</div>
      <div className="mt-0.5 text-sm text-text-primary">{children}</div>
    </div>
  );
}
